import React from 'react';
import { Plus, Star, Flame, Zap, Percent, ShoppingBag } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Product } from '../types';
import { useCart } from '../hooks/useCart';

interface ProductCardProps {
  product: Product;
}

export const ProductCard: React.FC<ProductCardProps> = ({ product }) => {
  const { addToCart } = useCart();
  const navigate = useNavigate();

  const discountPercentage = Math.round(((product.originalPrice - product.currentPrice) / product.originalPrice) * 100);

  const handleCardClick = () => {
    navigate(`/product/${product.id}`);
  };

  return (
    <div 
      onClick={handleCardClick}
      className="group flex flex-col h-full bg-white border border-stone-200 rounded-xl md:rounded-2xl overflow-hidden hover:shadow-xl hover:border-stone-300 transition-all duration-500 cursor-pointer w-full relative"
    >
      
      {/* Premium Badges - Highly Responsive */}
      <div className="absolute top-1.5 left-1.5 md:top-3 md:left-3 z-10 flex flex-col gap-1 md:gap-1.5">
        {product.isNew && (
          <span className="px-1.5 py-0.5 md:px-2.5 md:py-1 bg-white/90 backdrop-blur-sm text-stone-800 text-[7px] md:text-[10px] font-medium uppercase tracking-widest rounded md:rounded-md border border-stone-200 flex items-center gap-0.5 md:gap-1.5 shadow-sm">
            <Zap className="w-2 h-2 md:w-2.5 md:h-2.5 text-orange-500" /> NEW
          </span>
        )}
        {product.isTrending && (
          <span className="px-1.5 py-0.5 md:px-2.5 md:py-1 bg-white/90 backdrop-blur-sm text-stone-800 text-[7px] md:text-[10px] font-medium uppercase tracking-widest rounded md:rounded-md border border-stone-200 flex items-center gap-0.5 md:gap-1.5 shadow-sm">
            <Flame className="w-2 h-2 md:w-2.5 md:h-2.5 text-orange-500" /> TRENDING
          </span>
        )}
        {discountPercentage > 0 && (
          <span className="px-1.5 py-0.5 md:px-2.5 md:py-1 bg-orange-50/90 backdrop-blur-sm text-orange-800 text-[7px] md:text-[10px] font-medium uppercase tracking-widest rounded md:rounded-md border border-orange-200/50 flex items-center gap-0.5 md:gap-1.5 shadow-sm">
            <Percent className="w-2 h-2 md:w-2.5 md:h-2.5" strokeWidth={2} /> {discountPercentage}% OFF
          </span>
        )}
      </div>

      {/* Image Container */}
      <div className="relative aspect-square overflow-hidden bg-stone-50">
        {product.images && product.images.length > 0 ? (
          <img 
            src={product.images[0]} 
            alt={product.name}
            className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-stone-300">
            <ShoppingBag className="w-8 h-8 md:w-10 md:h-10" strokeWidth={1} />
          </div>
        )}
        
        {/* Elegant Quick Add Button */}
        <button 
          onClick={(e) => {
            e.stopPropagation();
            addToCart(product);
          }}
          className="absolute bottom-2 right-2 p-2 md:bottom-4 md:right-4 md:p-3.5 bg-white/90 backdrop-blur-md text-stone-800 rounded-full shadow-md border border-stone-200 hover:bg-orange-600 hover:text-white hover:border-orange-600 transition-all opacity-100 md:opacity-0 md:group-hover:opacity-100 md:translate-y-2 md:group-hover:translate-y-0"
          aria-label="Add to Cart"
        >
          <Plus className="w-4 h-4 md:w-4 md:h-4" strokeWidth={2} />
        </button>
      </div>

      {/* Product Details */}
      <div className="p-3 md:p-5 flex flex-col flex-grow bg-white">
        <div className="flex justify-between items-start mb-1.5 md:mb-2">
          <h3 className="text-[9px] md:text-xs font-medium uppercase tracking-widest text-stone-500 line-clamp-1 mr-1">
            {product.category}
          </h3>
          <div className="flex items-center gap-0.5 md:gap-1 text-stone-500 shrink-0">
             <Star className="w-2 h-2 md:w-3 md:h-3 fill-orange-400 text-orange-400" />
             <span className="text-[10px] md:text-xs font-medium">{product.rating}</span>
          </div>
        </div>

        <h2 className="text-xs md:text-base font-medium text-stone-800 mb-2 md:mb-4 leading-relaxed line-clamp-2 md:min-h-[3em]">
          {product.name}
        </h2>

        {/* Price Section */}
        <div className="mt-auto flex items-end gap-1.5 md:gap-3">
          <div className="flex items-end gap-0.5">
            <span className="text-xs md:text-sm font-medium text-stone-800 mb-0.5">₹</span>
            <span className="text-sm md:text-xl font-medium text-stone-900 leading-none">
              {product.currentPrice.toLocaleString('en-IN')}
            </span>
          </div>
          
          {product.originalPrice > product.currentPrice && (
            <span className="text-[10px] md:text-sm text-stone-400 line-through mb-0.5 font-normal">
              ₹{product.originalPrice.toLocaleString('en-IN')}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};
